import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Button } from '@progress/kendo-react-buttons';
import { Window } from '@progress/kendo-react-dialogs';
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab
} from '@progress/kendo-react-layout';
import { EditorUtils } from '@progress/kendo-react-editor';
import { Media } from '../../types/media';
import { Col, Row } from 'react-bootstrap';
import { insertMediaFiles, insertVideoPlatform } from './insert-image-plugin';
import { Error, Label } from '@progress/kendo-react-labels';
import { capitalize } from 'lodash';
import { GameDocumentContext } from '../../contexts/game-document';
import {
  Checkbox,
  NumericTextBox,
  RadioButton
} from '@progress/kendo-react-inputs';
import { StandardInput } from '../forms';
import { Resource } from '../../types/game-document';
import {
  AddResourceAsync,
  UpdateGameDocState
} from '../../utils/game-document';
import { uuid } from '../../types/common-helper';
import { ResourceTab } from './resource-tab';

export const fileSize = {
  image: 512000, // 500KB
  audio: 5242880, // 5MB
  video: 20971520 // 20MB
};

const fileSizeDesc = {
  image: '500KB',
  audio: '5MB',
  video: '20MB'
};

const fileType = {
  image: 'image/svg, image/jpeg, image/jpg, image/png, image/gif, image/bmp',
  video: 'video/webm, video/mov, video/mpeg, video/mp4, video/avi',
  audio: 'audio/webm, audio/wav, audio/ogg, audio/mp3'
};

export type VideoPlatform = 'youtube' | 'vimeo' | null;
export const detectVideoPlatform = (
  url: string
): { platform: VideoPlatform; id?: string } => {
  // YouTube formats
  const youtubeMatch = url.match(
    /(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );

  if (youtubeMatch) {
    return {
      platform: 'youtube',
      id: youtubeMatch[1]
    };
  }

  // Vimeo formats
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);

  if (vimeoMatch) {
    return {
      platform: 'vimeo',
      id: vimeoMatch[1]
    };
  }

  return { platform: null };
};

export const InsertMediaDialog = (props: any) => {
  const [game, setGame] = React.useContext(GameDocumentContext);
  const [selected, setSelected] = React.useState(0);
  const [files, setFiles] = React.useState<Media[]>([]);
  const fileUpload = React.useRef<HTMLInputElement>(null);
  const [isLargeMediaSize, setIsLargeMediaSize] =
    React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [mediaProps, setMediaProps] = React.useState<{
    alt: string;
    title: string;
    width: number | null;
    height: number | null;
    class?: string;
    src?: string;
  }>({
    alt: '',
    title: '',
    height: null,
    width: null
  });
  const [fitToScreen, setFitToScreen] = React.useState<boolean>(false);

  const toggleIsSaving = () => setIsSaving((prev) => !prev);

  const onTabSelect = (event: TabStripSelectEventArguments) => {
    setIsLargeMediaSize(false);
    setFiles([]);
    setMediaProps((prev) => ({ ...prev, src: undefined }));
    setSelected(event.selected);
  };

  const onClose = () => {
    props.onClose();
  };

  const onUploadPicture = (fls: FileList | null) => {
    if (fls) {
      // check image max size for Game Design | Assets - Task Content - HTML
      const maxImageSizeInByte = fileSize[mediaType as keyof typeof fileSize]; // 500 KB for image, 2MB for video and audio

      if (fls[0].size > maxImageSizeInByte) {
        setIsLargeMediaSize(true);
        return;
      }

      setIsLargeMediaSize(false);
      setFiles([{ file: fls[0] }]);
    }
  };

  const onInsert = (currentSrc: string | null) => {
    if (!game) return;
    if (!mediaProps.src && !currentSrc && !files.length) return;

    const gameId = game.gameId;
    const currentVersion = game.gameDocument?.version;
    const { view, mediaType } = props;
    const nodes = view.state.schema.nodes;
    let nodeType = nodes[mediaType];
    const position = null;
    const data = {
      src: mediaProps.src ? mediaProps.src : currentSrc,
      title: mediaProps.title,
      alt: mediaProps.alt,
      width: !fitToScreen ? mediaProps.width : '100%',
      height: !fitToScreen ? mediaProps.height : 'auto',
      ...(mediaType === 'video' && {
        ['fit-to-screen']: fitToScreen
      }),
      ...(mediaType === 'image' && {
        class: mediaProps.class
      })
    } as any;

    const videoPlatform = detectVideoPlatform(mediaProps.src ?? '');

    const attrs: Record<string, any> = Object.keys(data)
      .filter((key) => data[key] !== null && data[key] !== '')
      .reduce((acc, curr) => Object.assign(acc, { [curr]: data[curr] }), {});

    if (files.length && !selected) {
      toggleIsSaving();
      insertMediaFiles({
        view,
        files,
        nodeType,
        position,
        gameId,
        version: currentVersion,
        toggleSaving: toggleIsSaving,
        onClose,
        ...(props.resourceCategory && {
          onAddToResource: (newResource: Resource, fileName: string) => {
            const resourceId = uuid();
            AddResourceAsync(
              game.gameDocument!,
              attrs.title ?? fileName,
              attrs.title ?? fileName,
              nodeType.name,
              newResource.value,
              resourceId,
              newResource.size,
              props.resourceCategory
            ).then((response) =>
              setGame((prev) => UpdateGameDocState(prev, response))
            );
          }
        }),
        attrs
      });
    } else if (videoPlatform.platform) {
      nodeType =
        videoPlatform.platform === 'youtube'
          ? nodes['youtube']
          : nodes['vimeo'];
      insertVideoPlatform({
        view,
        nodeType,
        videoId: videoPlatform.id,
        onClose,
        attrs
      });
    } else {
      if (attrs.src && props.resourceCategory && selected !== 2) {
        const resourceId = uuid();
        AddResourceAsync(
          game.gameDocument!,
          attrs.title ?? `html-editor${nodeType.name}-media`,
          attrs.title ?? ``,
          nodeType.name,
          attrs.src,
          resourceId,
          0,
          props.resourceCategory
        ).then((response) =>
          setGame((prev) => UpdateGameDocState(prev, response))
        );
      }
      const newMedia = nodeType.create(attrs);
      EditorUtils.insertNode(view, newMedia, true);
      view.focus();
      onClose();
    }
  };

  const { view, mediaType } = props;
  const state = view && view.state;

  React.useEffect(() => {
    if (
      state &&
      state.selection.node &&
      state.selection.node.type === state.schema.nodes[mediaType]
    ) {
      const nodeAttrs = state.selection.node.attrs;

      const isFitToScreen =
        nodeAttrs.width === '100%' && nodeAttrs.height === 'auto';

      if (isFitToScreen) setFitToScreen(true);

      setMediaProps(nodeAttrs);
    }
  }, [state]);

  const fields = (
    <React.Fragment>
      <div className="k-edit-label">
        <label htmlFor="k-editor-image-alt">Alternate text</label>
      </div>
      <div className="k-edit-field">
        <StandardInput
          value={mediaProps.alt}
          onChange={(e) => setMediaProps((prev) => ({ ...prev, alt: e.value }))}
        />
      </div>
      <div className="k-edit-label">
        <label htmlFor="k-editor-image-title">Title</label>
      </div>
      <div className="k-edit-field">
        <StandardInput
          value={mediaProps.title}
          onChange={(e) =>
            setMediaProps((prev) => ({ ...prev, title: e.value }))
          }
        />
      </div>

      {props.resizeMedia && (
        <div>
          {mediaType === 'video' && (
            <>
              <div className="k-edit-label">Fit to screen</div>
              <div className="k-edit-field d-flex align-items-start mt-1">
                <Checkbox
                  name={'checkbox-use-default'}
                  value={fitToScreen}
                  onChange={(e) => {
                    setMediaProps((prev) => ({
                      ...prev,
                      width: null,
                      height: null
                    }));
                    setFitToScreen(e.value);
                  }}
                />
              </div>
            </>
          )}
          <div className="k-edit-label">
            <label htmlFor="k-editor-image-width">Width (px)</label>
          </div>
          <div className="k-edit-field">
            <NumericTextBox
              disabled={fitToScreen}
              min={100}
              max={4000}
              value={fitToScreen ? null : mediaProps.width}
              onChange={(e) =>
                setMediaProps((prev) => ({
                  ...prev,
                  width: e.value
                }))
              }
            />
          </div>
          <div className="k-edit-label">
            <label htmlFor="k-editor-image-height">Height (px)</label>
          </div>
          <div className="k-edit-field">
            <NumericTextBox
              disabled={fitToScreen}
              min={100}
              max={4000}
              value={fitToScreen ? null : mediaProps.height}
              onChange={(e) =>
                setMediaProps((prev) => ({
                  ...prev,
                  height: e.value
                }))
              }
            />
          </div>
        </div>
      )}

      {mediaType === 'image' && (
        <React.Fragment>
          <div className="k-edit-label">
            <label htmlFor="k-editor-image-title">Float</label>
          </div>
          <div className="k-edit-field mt-2 row p-0 m-0">
            <div className="col p-0 m-0">
              <RadioButton
                label="None"
                name="image-format"
                checked={
                  !mediaProps.class ||
                  ['', 'float-none'].includes(mediaProps.class)
                }
                onChange={(e) =>
                  setMediaProps((prev) => ({
                    ...prev,
                    class: 'float-none'
                  }))
                }
              />
            </div>
            <div className="col d-flex p-0 m-0">
              <RadioButton
                id="float-left"
                name="image-format"
                checked={
                  mediaProps.class !== undefined &&
                  mediaProps.class === 'float-left'
                }
                onChange={(e) =>
                  setMediaProps((prev) => ({
                    ...prev,
                    class: 'float-left'
                  }))
                }>
                <Label
                  editorId="float-left"
                  className="d-flex flex-column fw-normal cursor-pointer text-decoration-none">
                  <span>Left</span>
                  <span>
                    <span
                      className="material-symbols-outlined"
                      style={{
                        fontSize: '32px'
                      }}>
                      format_image_left
                    </span>
                  </span>
                </Label>
              </RadioButton>
            </div>
            <div className="col d-flex p-0 m-0">
              <RadioButton
                id="float-right"
                name="image-format"
                checked={
                  mediaProps.class !== undefined &&
                  mediaProps.class === 'float-right'
                }
                onChange={(e) =>
                  setMediaProps((prev) => ({
                    ...prev,
                    class: 'float-right'
                  }))
                }>
                <Label
                  editorId="float-right"
                  className="d-flex flex-column fw-normal cursor-pointer text-decoration-none">
                  <span>Right</span>
                  <span>
                    <span
                      className="material-symbols-outlined"
                      style={{
                        fontSize: '32px'
                      }}>
                      format_image_right
                    </span>
                  </span>
                </Label>
              </RadioButton>
            </div>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );

  const limitWidthAndHeight = React.useMemo(() => {
    if (props.mediaType !== 'video') return false;
    if (fitToScreen) return false;
    if (
      typeof mediaProps.height === 'number' &&
      typeof mediaProps.width !== 'number'
    )
      return true;
    if (
      typeof mediaProps.width === 'number' &&
      typeof mediaProps.height !== 'number'
    )
      return true;

    if (mediaProps.width === null && mediaProps.height === null) return false;
  }, [props.mediaType, fitToScreen, mediaProps.height, mediaProps.width]);

  const buttons = (
    <Row className="mt-3">
      <Col md="12" className="d-flex justify-content-end align-items-center">
        <Button
          themeColor={'secondary'}
          className={'w-5 text-light'}
          disabled={isSaving}
          onClick={onClose}>
          Cancel
        </Button>

        <Button
          themeColor={'primary'}
          className={'text-light ml-2'}
          disabled={isLargeMediaSize || limitWidthAndHeight || isSaving}
          onClick={() => onInsert(mediaProps.src ? mediaProps.src : null)}>
          {isSaving && (
            <span
              className={`k-icon k-i-loading mr-2`}
              style={{
                fontSize: '20px',
                color: 'white'
              }}
            />
          )}
          {isSaving ? 'Saving' : 'Save'}
        </Button>
      </Col>
    </Row>
  );

  const onUploadClick = () => {
    if (fileUpload && fileUpload.current) {
      fileUpload.current.click();
    }
  };

  const handledelete = (file: Media) => {
    if (files) {
      const fls: Media[] = [...files];

      const index = fls.indexOf(file);
      if (index > -1) {
        fls.splice(index, 1);
      }
      setFiles(fls);
      setIsLargeMediaSize(false);
    }
  };

  return (
    <>
      {ReactDOM.createPortal(
        <Window
          title={`Insert ${capitalize(mediaType)}`}
          onClose={onClose}
          resizable={false}
          draggable={false}
          initialWidth={540}
          initialHeight={540}>
          <TabStrip
            selected={selected}
            onSelect={onTabSelect}
            animation={false}>
            {state && state.selection && !state.selection.node && (
              <TabStripTab title="Upload">
                <div className="k-edit-form-container pt-3 pb-3">
                  <div className="k-edit-label">
                    <label htmlFor="k-editor-image-width">
                      {capitalize(mediaType)}
                    </label>
                  </div>
                  <div className="k-edit-field">
                    <div className={'col-md-6'}>
                      <Button
                        className={'w-100 upload-button mt-2 mb-2'}
                        onClick={onUploadClick}>
                        <div
                          className={
                            'd-flex justify-content-center align-items-center gap-3'
                          }>
                          <span
                            className={
                              'material-symbols-outlined mr-2 text-success'
                            }
                            role={'button'}>
                            {(() => {
                              switch (mediaType) {
                                case 'image':
                                  return 'add_photo_alternate';
                                case 'video':
                                  return 'video_file';
                                case 'audio':
                                  return 'audio_file';
                              }
                            })()}
                          </span>
                          <span>Upload</span>
                        </div>
                      </Button>
                      <input
                        type={'file'}
                        id={'file-upload'}
                        ref={fileUpload}
                        multiple={false}
                        accept={fileType[mediaType as keyof typeof fileType]}
                        className={'d-none'}
                        onChange={(e) => onUploadPicture(e.target.files)}
                        onClick={(event) => {
                          event.currentTarget.value = '';
                        }}
                      />
                      {isLargeMediaSize && (
                        <Error>{`Please reduce your ${mediaType} size below ${
                          fileSizeDesc[mediaType as keyof typeof fileSizeDesc]
                        }`}</Error>
                      )}
                    </div>
                    <div className={'col-12'}>
                      <ul className={'list-group w-100'}>
                        {files &&
                          files.map((file, i) => {
                            return (
                              <li
                                className={'list-group-item rounded-0'}
                                key={i}>
                                <div
                                  className={'d-flex justify-content-between'}>
                                  <div className={'d-flex'}>
                                    <span
                                      className={
                                        'material-symbols-outlined mr-2 p-2'
                                      }>
                                      add_photo_alternate
                                    </span>
                                    <span className={'p-2 text-break'}>
                                      {file.file.name}
                                    </span>
                                  </div>
                                  <span
                                    className={
                                      'material-symbols-outlined mr-2 p-2'
                                    }
                                    role={'button'}
                                    onClick={() => handledelete(file)}>
                                    close
                                  </span>
                                </div>
                              </li>
                            );
                          })}
                      </ul>
                    </div>
                  </div>
                  {fields}
                </div>
              </TabStripTab>
            )}
            <TabStripTab title="By URL">
              <div className="k-edit-form-container pt-3 pb-3">
                <div className="k-edit-label">
                  <label htmlFor="k-editor-image-url">Web address</label>
                </div>
                <div className="k-edit-field">
                  <StandardInput
                    value={mediaProps.src}
                    disabled={
                      new RegExp(`/^data:${mediaType}/`).test(
                        mediaProps.src || ''
                      ) ||
                      (state && state.selection && state.selection.node)
                    }
                    autoFocus={true}
                    onChange={(e) =>
                      setMediaProps((prev) => ({ ...prev, src: e.value }))
                    }
                  />
                </div>
                {fields}
              </div>
            </TabStripTab>
            <TabStripTab title="Media Library">
              <ResourceTab
                mediaType={props.mediaType}
                fields={fields}
                onChange={(value) =>
                  setMediaProps((prev) => ({ ...prev, src: value }))
                }
              />
            </TabStripTab>
          </TabStrip>
          {buttons}
          <style>{`.k-dropzone { width: 100%; }`}</style>
        </Window>,
        document.body,
        'insertImageDialog'
      )}
    </>
  );
};
